import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../../../components/Modal';
import { Button } from '../../../components/Button';
import { listProducts } from '../../../api/endpoints';
import type { SearchResult } from '../../../types/api';

interface Props {
  /** 一括登録の対象になる明細の件数 */
  targetCount: number;
  /** 行を選んでいるか（選んでいなければ表示中の明細すべてが対象） */
  hasSelection: boolean;
  spotCode: string;
  onClose: () => void;
  onSelectSpot: () => void;
  onSelectCode: (product: SearchResult) => void;
}

interface GenericCode {
  id: string;
  name1: string;
  name2: string;
  cat1: string; cat1Name: string;
  cat2: string; cat2Name: string;
  cat3: string; cat3Name: string;
}

interface Option { code: string; name: string }

// 1回の取得件数。汎用コード（名称ﾏﾆｭｱﾙ入力区分='1'）は商品マスタ全体の一部
// （MVPで49,109件中3件）なので、開いたときに全件取り、分類・語句の絞り込みは画面側で行う。
const FETCH_PER_PAGE = 500;

// 全角半角・大小文字を区別せずに絞り込む（商品検索の search_norm と同じ考え方）
const norm = (s: string) => s.normalize('NFKC').toLowerCase();

/** 分類の選択肢。汎用コードを持つ分類だけを、コード順に重複なく並べる。 */
function optionsOf(list: GenericCode[], code: (g: GenericCode) => string, name: (g: GenericCode) => string): Option[] {
  const map = new Map<string, string>();
  list.forEach((g) => { if (code(g)) map.set(code(g), name(g) || code(g)); });
  return [...map].sort(([a], [b]) => a.localeCompare(b)).map(([c, n]) => ({ code: c, name: n }));
}

/**
 * 見積の明細にコードを一括登録する。仮コード(99999950)か、名称を書き換えてよい
 * 汎用コード（名称ﾏﾆｭｱﾙ入力区分='1'）を選ぶ。どちらも商品名はOCRの品名のまま残す。
 * 汎用コードは分類1〜3と語句で絞り込める。
 */
export function BulkCodeModal({ targetCount, hasSelection, spotCode, onClose, onSelectSpot, onSelectCode }: Props) {
  const [all, setAll] = useState<GenericCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [cat1, setCat1] = useState('');
  const [cat2, setCat2] = useState('');
  const [cat3, setCat3] = useState('');

  // 開いたら汎用コードを全件取る
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const out: GenericCode[] = [];
        for (let page = 1; ; page++) {
          const data = await listProducts({ q: '', page, per_page: FETCH_PER_PAGE, editableOnly: true });
          out.push(...data.items.map((it: Record<string, string>) => ({
            id: it['商品コード'] || '',
            name1: it['商品名1'] || '',
            name2: it['商品名2'] || '',
            cat1: it['分類コード1'] || '', cat1Name: it['分類名称1'] || '',
            cat2: it['分類コード2'] || '', cat2Name: it['分類名称2'] || '',
            cat3: it['分類コード3'] || '', cat3Name: it['分類名称3'] || '',
          })));
          if (data.items.length === 0 || out.length >= data.total) break;
        }
        if (!cancelled) setAll(out);
      } catch {
        if (!cancelled) setError('汎用コードを取得できませんでした。時間をおいてもう一度お試しください。');
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // 上位の分類を選ぶと、下位の選択肢はその中にある分類だけになる
  const cat1Options = useMemo(() => optionsOf(all, (g) => g.cat1, (g) => g.cat1Name), [all]);
  const cat2Options = useMemo(
    () => optionsOf(all.filter((g) => g.cat1 === cat1), (g) => g.cat2, (g) => g.cat2Name),
    [all, cat1],
  );
  const cat3Options = useMemo(
    () => optionsOf(all.filter((g) => g.cat1 === cat1 && g.cat2 === cat2), (g) => g.cat3, (g) => g.cat3Name),
    [all, cat1, cat2],
  );

  const shown = useMemo(() => {
    const terms = norm(query).split(/\s+/).filter(Boolean);
    return all.filter((g) =>
      (!cat1 || g.cat1 === cat1) && (!cat2 || g.cat2 === cat2) && (!cat3 || g.cat3 === cat3)
      && terms.every((t) => norm(`${g.id} ${g.name1} ${g.name2} ${g.cat3Name}`).includes(t)),
    );
  }, [all, query, cat1, cat2, cat3]);

  const select = (g: GenericCode) =>
    onSelectCode({ id: g.id, name1: g.name1, name2: g.name2, category: g.cat3Name, name_editable: true });

  const selectClass = 'border rounded px-2 py-1.5 text-sm min-w-[140px] bg-white';

  return (
    <Modal open={true} onClose={onClose} title="コード一括登録" widthClass="max-w-3xl">
      <p className="text-sm mb-3">
        {hasSelection ? 'チェックした' : '表示中の'}明細 <strong>{targetCount}</strong> 件に、選んだコードを登録します。
        商品名・寸法はOCRの読取値のまま残ります。
      </p>

      <div className="bg-slate-50 p-3 rounded mb-3 flex items-center justify-between gap-2">
        <span className="text-sm">仮コード <span className="font-mono">{spotCode}</span></span>
        <Button onClick={onSelectSpot} disabled={targetCount === 0}>仮コードで登録</Button>
      </div>

      <div className="bg-slate-50 p-3 rounded">
        <p className="text-sm font-medium mb-2">汎用コードから選ぶ（名称マニュアル入力区分が「1」のコード）:</p>
        <div className="flex flex-wrap gap-2 items-end mb-2">
          <div>
            <label className="text-xs text-muted block mb-1">分類1</label>
            <select className={selectClass} value={cat1}
              onChange={(e) => { setCat1(e.target.value); setCat2(''); setCat3(''); }}>
              <option value="">すべて</option>
              {cat1Options.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted block mb-1">分類2</label>
            <select className={selectClass} value={cat2} disabled={!cat1}
              onChange={(e) => { setCat2(e.target.value); setCat3(''); }}>
              <option value="">すべて</option>
              {cat2Options.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted block mb-1">分類3</label>
            <select className={selectClass} value={cat3} disabled={!cat2}
              onChange={(e) => setCat3(e.target.value)}>
              <option value="">すべて</option>
              {cat3Options.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="text-xs text-muted block mb-1">語句</label>
            <input
              type="text"
              className="border rounded px-2 py-1.5 text-sm w-full"
              placeholder="商品コード・商品名で絞り込み..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>
        {loading && <p className="text-sm text-muted">汎用コードを読み込み中...</p>}
        {error && <p className="text-danger text-sm mb-2">{error}</p>}
        {!loading && !error && shown.length === 0 && (
          <p className="text-sm text-muted">該当する汎用コードはありません。</p>
        )}
        {shown.length > 0 && (
          <>
            <p className="text-xs text-muted mb-1">{all.length}件中 {shown.length}件</p>
            <div className="overflow-auto max-h-[40vh]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b">
                    <th className="pb-1 pr-2">商品コード</th>
                    <th className="pb-1 pr-2">商品名</th>
                    <th className="pb-1 pr-2">商品名2</th>
                    <th className="pb-1 pr-2">分類</th>
                    <th className="pb-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((g) => (
                    <tr key={g.id} className="border-b last:border-0 hover:bg-blue-50">
                      <td className="py-1 pr-2 font-mono text-xs">{g.id}</td>
                      <td className="py-1 pr-2">{g.name1}</td>
                      <td className="py-1 pr-2 text-xs text-muted">{g.name2}</td>
                      <td className="py-1 pr-2 text-xs text-muted">
                        {[g.cat1Name, g.cat2Name, g.cat3Name].filter(Boolean).join(' / ')}
                      </td>
                      <td className="py-1">
                        <Button className="text-xs px-2 py-0.5" disabled={targetCount === 0} onClick={() => select(g)}>
                          このコードで登録
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
